import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { AnnouncementPopup } from '../../services/announcementService';

const POPUP_ICONS: Record<string, string> = {
  rocket: '\u{1F680}',
  bell: '\u{1F514}',
  gift: '\u{1F381}',
  star: '\u2B50',
  clock: '\u23F0',
  refresh: '\u{1F504}',
  'credit-card': '\u{1F4B3}',
};

const STYLE_COLORS: Record<string, { bg: string; border: string; icon: string }> = {
  info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  success: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600' },
};

const isCustomHexColor = (style?: string): boolean => {
  return !!style && style.startsWith('#');
};

interface AnnouncementModalProps {
  announcement: AnnouncementPopup;
  onDismiss: () => void;
  onCtaClick: () => void;
  onPageCtaClick?: (url: string) => void;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  announcement,
  onDismiss,
  onCtaClick,
  onPageCtaClick,
}) => {
  const { t } = useTranslation();
  const config = announcement.popupConfig;
  const isCustomColor = isCustomHexColor(config.style);
  const style = isCustomColor ? null : (STYLE_COLORS[config.style || 'info'] || STYLE_COLORS.info);
  const customBgColor = isCustomColor ? config.style : undefined;
  const icon = config.icon ? POPUP_ICONS[config.icon] || '\u{1F4E2}' : '\u{1F4E2}';
  const coverType = config.coverType || 'icon';
  const showBackButton = config.showBackButton !== false;

  // Multi-page support
  const pages = config.pages && config.pages.length > 0 ? config.pages : null;
  const totalPages = pages ? pages.length : 1;
  const [currentPage, setCurrentPage] = useState(0);
  const isLastPage = currentPage >= totalPages - 1;
  const isFirstPage = currentPage === 0;

  // Resolve per-page data (falls back to single-page fields)
  const pageTitle = pages ? pages[currentPage].title : announcement.title;
  const pageContent = pages ? pages[currentPage].content : announcement.content;
  const pageImageUrl = pages ? pages[currentPage].imageUrl : config.imageUrl;
  const pageCtaOverride = pages ? pages[currentPage].ctaOverride : undefined;

  // Button logic:
  // LEFT: first page → dismiss, otherwise → back (if showBackButton), otherwise → nothing
  // RIGHT: page has ctaOverride → custom CTA, last page / single → global CTA, otherwise → next
  const getLeftButton = () => {
    if (isFirstPage) {
      const text = config.secondaryText || t('common:actions.dismiss', 'Dismiss');
      return { text, action: onDismiss };
    }
    if (showBackButton) {
      return { text: t('common:actions.back', 'Back'), action: () => setCurrentPage(prev => prev - 1) };
    }
    return null;
  };

  const getRightButton = () => {
    // Non-last page with ctaOverride
    if (pages && !isLastPage && pageCtaOverride?.text && pageCtaOverride?.url) {
      return {
        text: pageCtaOverride.text,
        action: () => {
          if (onPageCtaClick) {
            onPageCtaClick(pageCtaOverride.url);
          } else {
            onDismiss();
          }
        },
      };
    }
    // Last page or single page → global CTA
    if (isLastPage || !pages) {
      if (config.ctaText) {
        return { text: config.ctaText, action: onCtaClick };
      }
      return null;
    }
    // Non-last page, no override → Next
    return { text: t('common:actions.next', 'Next'), action: () => setCurrentPage(prev => prev + 1) };
  };

  const leftBtn = getLeftButton();
  const rightBtn = getRightButton();

  // If no right button (no CTA on last page), show dismiss as full-width primary
  const showDismissAsPrimary = !rightBtn && isLastPage;

  return (
    <Dialog open onClose={onDismiss} className="relative z-[60]">
      <div className="fixed inset-0 bg-black/40 transition-opacity" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative mx-auto max-w-sm w-full bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all">
          {/* X close button */}
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 z-10 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label={t('common:actions.close', 'Close')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Cover Section (Icon or Image) + Title */}
          <div
            key={`header-${currentPage}`}
            className={`${style ? `${style.bg} ${style.border}` : 'border-gray-200'} border-b px-6 pt-8 pb-4 text-center announcement-page-content`}
            style={customBgColor ? { backgroundColor: customBgColor } : undefined}
          >
            {coverType === 'icon' ? (
              <div className="text-5xl mb-3">{icon}</div>
            ) : pageImageUrl ? (
              <div className="mb-3">
                <img
                  src={pageImageUrl}
                  alt=""
                  className="w-full rounded-lg object-cover max-h-48 mx-auto"
                />
              </div>
            ) : null}
            <Dialog.Title className="text-xl font-bold text-gray-900">
              {pageTitle}
            </Dialog.Title>
          </div>

          {/* Content */}
          <div key={`content-${currentPage}`} className="px-6 py-5 announcement-page-content">
            <div
              className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: pageContent || '' }}
            />
          </div>

          {/* Actions — 2-button horizontal layout */}
          <div className="px-6 pb-4 flex gap-3">
            {showDismissAsPrimary ? (
              <button
                onClick={onDismiss}
                className="btn-primary w-full justify-center"
              >
                {config.secondaryText || t('common:actions.dismiss', 'Dismiss')}
              </button>
            ) : (
              <>
                {leftBtn && (
                  <button
                    onClick={leftBtn.action}
                    className="btn-ghost flex-1 justify-center text-sm text-gray-500"
                  >
                    {leftBtn.text}
                  </button>
                )}
                {rightBtn && (
                  <button
                    onClick={rightBtn.action}
                    className="btn-primary flex-1 justify-center"
                  >
                    {rightBtn.text}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Dot indicators (multi-page only, below buttons) */}
          {pages && totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 pb-4">
              {pages.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    idx === currentPage ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </Dialog.Panel>
      </div>

      {/* Page transition animation */}
      <style>{`
        .announcement-page-content {
          animation: announcementFadeIn 200ms ease-in;
        }
        @keyframes announcementFadeIn {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </Dialog>
  );
};

export default AnnouncementModal;
